import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  NV_BRAND_UNDERLINE_CLASS,
  NV_PATHS,
  NV_PRIMARY_DISABLED_CLASS,
  NvAlertIcon,
  NvCheckIcon,
  NvConsentBox,
  NvPersonIcon,
  nvButtonVariants,
  type NvConsentItem,
} from "@/components/navigator";
import { cn } from "@/lib/utils";
import { COMPANY_COUNTRIES, OPTIONAL_SIGNUP_TERMS, SIGNUP_TERMS } from "@/services/navigator/signup-service";

// 회원가입 공용 조각 (Figma ver.7 07 로그인·회원가입 · 설계서 Front v0.5 slide 88~97): 카드 640 · 안쪽 여백 45, 입력 48 · 반경 10.

export const COUNTRY_OPTIONS = COMPANY_COUNTRIES.map((c) => ({ value: c, label: c }));
export const DEFAULT_COUNTRY = "한국";

// 약관 4종 라벨 — 약관 전문 보기 팝업의 탭 이름과 같아요 (제3자 제공만 (선택))
export const TERMS_LABELS: Record<(typeof SIGNUP_TERMS)[number], string> = {
  service_terms: "이용약관",
  privacy_policy: "개인정보 처리방침",
  privacy_signup: "개인정보 수집 및 이용 동의서",
  supplier_contact_share: "개인정보 제3자 제공 동의서",
};

// 선택 동의 (미동의로도 가입 가능). 미동의로 가입 신청하면 "선택 동의 미동의 안내" 팝업을 거쳐요
export const OPTIONAL_TERMS_CODE = OPTIONAL_SIGNUP_TERMS[0];
export const hasOptionalConsent = (consents: Record<string, boolean>) => !!consents[OPTIONAL_TERMS_CODE];

// 동의 항목 박스 줄: "이용약관 (필수)" … "개인정보 제3자 제공 동의서 (선택)" + 오른쪽 [전문 보기 ›]
export const SIGNUP_CONSENT_ITEMS: NvConsentItem[] = SIGNUP_TERMS.map((code) => ({
  code,
  label: `${TERMS_LABELS[code]} (${OPTIONAL_SIGNUP_TERMS.includes(code) ? "선택" : "필수"})`,
  viewable: true,
}));

// 약관 전문 보기 탭 이름 (Figma ver.7 847:47470)
export const SIGNUP_TERMS_TAB_LABELS = TERMS_LABELS;

// 선택 동의 미동의 안내 팝업 문구 (Figma ver.7 847:57555 그대로)
export const OPTIONAL_PROMPT = {
  title: "맞춤 에이전트 추천을 받아보세요",
  message: "동의하지 않으시면 에이전트 매핑 및 추천, 이에 따른 공급사와 연결 서비스는 제공되지 않을 수 있습니다.",
  itemLabel: "[선택] 개인정보 제3자 제공 동의서",
  declineLabel: "동의 없이 가입하기",
  agreeLabel: "동의하고 가입하기",
} as const;

// 전화번호: 숫자만 받고 ###-####-#### 로 자동 변환 (slide 88)
export const formatPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, d.length - 4)}-${d.slice(-4)}`;
};

export const formatBusinessNo = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

// 이름: 한글 5자 / 영문 20자 이내, 그 외 문자는 입력되지 않아요 (slide 88).
// 한글은 조합 중에 자음 · 모음(ㄱ · ㅏ)이 잠깐 들어오므로 입력에서는 남겨 두고, 완성 여부는 validName이 봐요
export function sanitizeName(raw: string) {
  if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(raw)) return raw.replace(/[^가-힣ㄱ-ㅎㅏ-ㅣ]/g, "").slice(0, 5);
  return raw.replace(/[^A-Za-z ]/g, "").replace(/^ +/, "").slice(0, 20);
}
export const validName = (name: string) => /^[가-힣]{1,5}$/.test(name) || /^[A-Za-z][A-Za-z ]{0,19}$/.test(name);
export const validEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export function SignupShell({ children, onSubmit }: { children: ReactNode; onSubmit: () => void }) {
  return (
    <div className="flex flex-1 justify-center bg-nv-bg-page px-4 pb-20 pt-14">
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="flex h-fit w-full max-w-[640px] flex-col gap-[18px] rounded-nv-card border border-nv-border bg-nv-surface px-6 py-10 md:px-11"
      >
        {children}
      </form>
    </div>
  );
}

export function SignupHeading({ title, description }: { title: string; description: string }) {
  return (
    <>
      <h1 className="text-[26px] font-normal leading-[31px] text-nv-text-primary">{title}</h1>
      <p className="text-[14px] leading-[17px] text-nv-text-secondary">{description}</p>
    </>
  );
}

// 가입 유형 설명 (Figma ver.7: 수요자 · 공급자 선택 칩 바로 아래 12/400 #6B7078)
export function SignupTypeHint() {
  return (
    <p className="text-[12px] leading-[14px] text-[#6B7078]">
      수요자 : 에이전트를 찾고 도입을 검토하는 기업 · 공급자 : 에이전트를 등록 · 운영하는 기업
    </p>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-[16px] font-medium leading-[19px] text-nv-text-primary">{children}</h2>;
}

// 약관 동의 박스 (Figma ver.7 "동의 항목" 550×193 · 흰색 · 선 · 반경 12): 모두 동의합니다 + 필수 3종 + 제3자 제공(선택), 각 줄 오른쪽 [전문 보기 ›]
export function TermsBox({ consents, onChange, onView }: { consents: Record<string, boolean>; onChange: (next: Record<string, boolean>) => void; onView: (code: string) => void }) {
  return <NvConsentBox items={SIGNUP_CONSENT_ITEMS} checked={consents} onChange={onChange} onView={onView} />;
}

export function SubmitRow({ label, disabled }: { label: string; disabled?: boolean }) {
  return (
    <div className="flex justify-center">
      <button type="submit" disabled={disabled} className={cn(nvButtonVariants({ variant: "primary", size: "md" }), NV_PRIMARY_DISABLED_CLASS)}>
        {label}
      </button>
    </div>
  );
}

export function LoginHint() {
  return (
    <p className="flex items-center justify-center gap-[6px] text-[13px] leading-4">
      <span className="text-nv-text-secondary">이미 회원이신가요?</span>
      <Link to={NV_PATHS.login} className={cn("font-bold text-nv-text-primary", NV_BRAND_UNDERLINE_CLASS)}>
        로그인하기
      </Link>
    </p>
  );
}

// 안내 화면(신청 완료 · 가입 완료 · 만료): 카드 520, 80px 원 아이콘 + 24/500 제목 + 14 설명 + [홈으로]
export function NoticeScreen({ tone, title, description, descriptionClassName, children }: { tone: "success" | "neutral" | "brand"; title: string; description: ReactNode; descriptionClassName?: string; children?: ReactNode }) {
  return (
    <div className="flex flex-1 justify-center bg-nv-bg-page px-4 pb-[120px] pt-24">
      <section className="flex h-fit w-full max-w-[520px] flex-col items-center gap-4 rounded-nv-card border border-nv-border bg-nv-surface px-10 py-12 text-center">
        <div aria-hidden className={cn("flex h-20 w-20 items-center justify-center rounded-full text-white", tone === "success" ? "bg-[#059669]" : tone === "brand" ? "bg-nv-brand" : "bg-[#999EA8]")}>
          {tone === "success" ? <NvCheckIcon size={39} /> : tone === "brand" ? <NvPersonIcon size={36} /> : <NvAlertIcon size={39} />}
        </div>
        <h1 className="text-[24px] font-medium leading-[29px] text-nv-text-primary">{title}</h1>
        <p className={cn("max-w-[367px] text-[14px] leading-[23px] text-nv-text-secondary", descriptionClassName)}>{description}</p>
        <div className="pt-2">{children ?? <HomeLink />}</div>
      </section>
    </div>
  );
}

export function HomeLink() {
  return (
    <Link to={NV_PATHS.home} className={cn(nvButtonVariants({ variant: "primary", size: "md" }), "")}>
      홈으로
    </Link>
  );
}
